import { useEffect, useMemo, useState } from 'react';

import { getCollaboratorsInProducts } from '../collaborators-in-products';

import { useInvitedAppAnalytics } from './invited-app-analytics';
import {
	type ExperimentDetails,
	type FullContext,
	IneligibilityReason,
	type TargetProduct,
	TargetProducts,
	type Traits,
} from './types';
import {
	type AccessMap,
	type GetAppAccessByShortcutsInput,
	getAppAccessFromProductLinksFilter,
	getAppAccessFromShortcuts,
	type GetAppAccessInput,
	hasAccessToAnyTargetProduct,
	ineligible,
	isFirstActiveExpiringSoonNeedsRefresh,
	type ResolvedCacheDetails,
	resolveExperimentDetails,
	resolveFromCache,
	updateActivation,
	updateCachedEligibility,
	updateCollaborators,
	updateFirstImpression,
} from './utils/invited-app-shortcuts-resolver';
import { useCanWriteToCache } from './utils/use-can-write-to-cache';

export type UseInvitedAppShortcutsInput =
	| ({ enabled: false } & Partial<UseInvitedAppShortcutsEnabledInput>)
	| ({ enabled: true } & UseInvitedAppShortcutsEnabledInput);

export type UseInvitedAppShortcutsEnabledInput = FullContext & {
	onCacheNeedsRefresh?: () => void;
} &
	// Either provide traits directly...
	(| {
				fetchTraits?: undefined;
				fetchTraitsPolicy?: undefined;
				traits: Traits | null;
		  }
		// ...or means to fetch them:
		| {
				fetchTraits: TraitsFetcher;
				fetchTraitsPolicy: 'always-fetch' | 'prefer-cache';
				traits?: undefined;
		  }
	);

export type UseInvitedAppShortcutsResult = ExperimentDetails & {
	registerActivation: (product: TargetProduct) => void;
	registerImpression: (input?: { products?: TargetProduct[] }) => void;
	updateFromAppAccessFilter: (input: GetAppAccessInput) => void;
	updateFromHydratedShortcuts: (input: GetAppAccessByShortcutsInput) => void;
};

type TraitsFetcher = ({ cloudId, userId }: { cloudId: string; userId: string }) => Promise<Traits>;

type TraitsState =
	| {
			status: 'complete';
			traits: Traits;
	  }
	| {
			status: 'loading' | 'initial';
			traits?: undefined;
	  }
	| {
			error: Error;
			status: 'error';
			traits?: undefined;
	  };

export const useInvitedAppShortcuts = ({
	enabled,
	sourceProduct,
	targetProducts,
	cloudId,
	userId,
	onCacheNeedsRefresh,
	traits: traitsArg,
	fetchTraits,
	fetchTraitsPolicy,
	cache,
}: UseInvitedAppShortcutsInput): UseInvitedAppShortcutsResult => {
	const { fireInvitedAppShortcutsEligibilityChecked } = useInvitedAppAnalytics();
	// TODO: add option that would pre-filter the app items that might be displayed in the menu
	// (based e.g. on license info or menu configuration)
	const canWriteToCache = useCanWriteToCache(enabled ? { enabled, cache } : { enabled: false });
	const [traitsState, setTraitsState] = useState<TraitsState>(() =>
		traitsArg ? { status: 'complete', traits: traitsArg } : { status: 'initial' },
	);
	const [eligibleAccess, setEligibleAccess] = useState<AccessMap | null>(null);
	const [cached] = useState<ResolvedCacheDetails>(() => {
		if (enabled) {
			try {
				return resolveFromCache({ sourceProduct, targetProducts, cache });

				// eslint-disable-next-line no-unused-vars
			} catch (_ignoredError) {
				return { eligibility: ineligible(IneligibilityReason.ERROR) };
			}
		} else {
			return { eligibility: ineligible(IneligibilityReason.EXPERIMENT_OFF) };
		}
	});

	// Fetch traits
	// if we've already established that user has eligible access to target products
	useEffect(() => {
		(async function maybeFetchTraits() {
			if (
				enabled &&
				userId &&
				canWriteToCache &&
				eligibleAccess &&
				traitsState.status === 'initial'
			) {
				const cacheNeedsRefresh = doesCacheNeedRefresh();
				if (cacheNeedsRefresh) {
					onCacheNeedsRefresh?.();
				}
				if (fetchTraits && (fetchTraitsPolicy === 'always-fetch' || cacheNeedsRefresh)) {
					setTraitsState({ status: 'loading' });
					try {
						const traits = await fetchTraits({ cloudId, userId });
						setTraitsState({ status: 'complete', traits });
					} catch (error) {
						setTraitsState({ status: 'error', error: error as Error });
					}
				}
			}
		})();

		function doesCacheNeedRefresh() {
			return (
				cached.eligibility.ineligibilityReason === IneligibilityReason.CACHE_EMPTY ||
				cached.eligibility.ineligibilityReason === IneligibilityReason.CACHE_EXPIRED ||
				isFirstActiveExpiringSoonNeedsRefresh(cached.state)
			);
		}
	}, [
		cached.eligibility,
		cached.state,
		canWriteToCache,
		cloudId,
		userId,
		enabled,
		eligibleAccess,
		fetchTraits,
		fetchTraitsPolicy,
		onCacheNeedsRefresh,
		traitsArg,
		traitsState.status,
	]);

	// Synchronize traits state with the supplied traits arg
	useEffect(() => {
		if (enabled && traitsState.status !== 'complete' && traitsArg) {
			setTraitsState({ status: 'complete', traits: traitsArg });
		}
	}, [enabled, traitsArg, traitsState.status]);

	// Update state once we got all the data to update eligibility
	useEffect(() => {
		(async function mainEligibilityUpdate() {
			if (enabled && userId && traitsState.status === 'complete' && eligibleAccess) {
				try {
					const updatedCacheState = await updateCachedEligibility({
						traits: traitsState.traits,
						access: eligibleAccess,
						cache,
					});
					const updatedEligibility = resolveExperimentDetails(
						{ sourceProduct, targetProducts },
						updatedCacheState,
					);
					const willNowBeEligible = updatedEligibility.ineligibilityReason === null;
					if (willNowBeEligible) {
						const products = TargetProducts;
						const collaboratorsAccess = await getCollaboratorsInProducts({
							products,
							cloudId,
							userId,
						});
						updateCollaborators({
							collaboratorsWithProducts: collaboratorsAccess,
							products,
							cache,
						});
					}
				} catch {
					// TODO: properly handle error
				}
			}
		})();
	}, [cache, enabled, eligibleAccess, traitsState, userId, cloudId, sourceProduct, targetProducts]);

	useEffect(() => {
		if (cached.eligibility.ineligibilityReason) {
			fireInvitedAppShortcutsEligibilityChecked({
				ineligibilityReason: cached.eligibility.ineligibilityReason,
			});
		}
	}, [cached.eligibility.ineligibilityReason, fireInvitedAppShortcutsEligibilityChecked]);

	return useMemo(() => {
		const getEligibleInvitedApps = () =>
			(Object.keys(cached.eligibility.invitedApps || {}) as TargetProduct[]).filter(
				(appName) => cached.eligibility.invitedApps?.[appName]?.eligible,
			);

		return {
			...cached.eligibility,

			/**
			 * Call this function when BFF yields a new version of the `getFilteredProductLinks` function.
			 *
			 * It will update our eligibility cache based on app access info from the BFF,
			 * using that filter function.
			 */
			updateFromAppAccessFilter: ({
				getFilteredProductLinks,
				limitToOnlyApsKeys = null,
			}: GetAppAccessInput) => {
				if (enabled && !eligibleAccess) {
					const access = getAppAccessFromProductLinksFilter({
						getFilteredProductLinks,
						limitToOnlyApsKeys,
					});
					if (hasAccessToAnyTargetProduct(access, targetProducts)) {
						setEligibleAccess(access);
					}
				}
			},

			updateFromHydratedShortcuts: ({
				shortcuts,
				limitToOnlyApsKeys,
			}: GetAppAccessByShortcutsInput) => {
				if (enabled && !eligibleAccess) {
					const access = getAppAccessFromShortcuts({ shortcuts, limitToOnlyApsKeys });
					if (hasAccessToAnyTargetProduct(access, targetProducts)) {
						setEligibleAccess(access);
					}
				}
			},

			registerImpression: ({ products = getEligibleInvitedApps() } = {}) => {
				if (enabled) {
					updateFirstImpression({ productsSeen: products, cache });
				}
			},

			registerActivation: (product: TargetProduct) => {
				if (enabled) {
					updateActivation({ product, cache });
				}
			},
		};
	}, [cache, cached.eligibility, enabled, targetProducts, eligibleAccess]);
};
